<template>
  <div v-if="userInfoLoaded" class="row justify-content-center">
    <div class="col-lg-6 col-md-8">
      <!-- User Profile Card -->
      <div class="card overflow-hidden mb-4">
        <div class="card-body p-0">
          <!-- Static Background Header -->
          <div class="profile-header d-flex flex-column align-items-center justify-content-center text-center text-white">
            <h2 class="mb-2 fw-bold">{{ $t('users.workersPermit') }}</h2>
            <h5 class="mb-0 fw-normal opacity-9">{{ headerName }} kt: {{ headerIdentificationNumber }}</h5>
          </div>

          <!-- User Info Section -->
          <div class="text-center px-4 pb-4" style="margin-top: -65px;">
            <a role="button" @click="onAvatarClick">
              <img
                class="avatar border border-4 border-white rounded-circle shadow"
                :src="avatarSrc"
                alt="User Avatar"
              />
            </a>
            <input ref="fileInput" type="file" accept="image/*" style="display: none" @change="handleFileChange" />
            <h5 class="mt-3 mb-1">{{ model.fullName }}</h5>
            <p v-if="userId === currentUserId" class="text-muted mb-1">
              <span class="fw-semibold opacity-75">Username:</span> {{ model.userName }}
            </p>
            <p class="text-muted mb-1">
              <span v-if="userId === currentUserId" class="fw-semibold opacity-75">Email:</span> <a :href="'mailto:' + model.email" class="text-decoration-none">{{ model.email }}</a>
            </p>
            <p class="text-muted mb-1">
              <a :href="'tel:' + model.phoneNumber" class="text-decoration-none">{{ model.phoneNumber }}</a>
            </p>
            <p v-if="model.employeeTitle" class="text-muted mb-0">
              {{ model.employeeTitle }}
            </p>
          </div>

          <!-- Edit Button -->
          <div v-if="!enableEdit && (userRole !== 'User' || userId === currentUserId)" class="text-center pb-4">
            <material-button fill color="dark" type="standard" @click="edit">
              {{ $t('users.editProfile') }}
            </material-button>
          </div>
        </div>
      </div>

      <!-- Edit Form Card -->
      <card v-if="enableEdit" :title="$t('users.editProfile')" class="mb-4" :on-cancel="cancel">
        <div class="row">
          <div class="col-md-8">
            <div class="mb-3">
              <material-input-field
                id="company"
                v-model="model.company"
                :label="$t('users.company')"
                :placeholder="$t('users.company')"
                name="company"
                variant="static"
                disabled
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <material-input-field
                id="companyId"
                v-model="companyModel.identificationNumber"
                :label="$t('users.identificationNumber')"
                :placeholder="$t('users.identificationNumber')"
                name="companyId"
                variant="static"
                disabled
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-8">
            <div class="mb-3">
              <material-input-field
                id="email"
                v-model="model.email"
                :label="$t('users.email')"
                type="email"
                :placeholder="$t('users.email')"
                name="email"
                variant="static"
                disabled
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label>{{ $t('users.role') }}</label>
              <el-select ref="roleInput" v-model="model.role" :class="selectClass" :disabled="!isRoleSelectable" class="w-100">
                <el-option
                  v-for="item in roleOptions"
                  :key="item"
                  :class="selectClass"
                  :value="item"
                  :disabled="!isRoleSelectable"
                />
              </el-select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-8">
            <div class="mb-3">
              <material-input-field
                id="employeeTitle"
                v-model="model.employeeTitle"
                :label="$t('users.employeeTitle')"
                :placeholder="$t('users.employeeTitle')"
                name="employeeTitle"
                variant="static"
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <material-input-field
                id="identificationNumber"
                v-model="model.identificationNumber"
                :label="$t('users.identificationNumber')"
                :placeholder="$t('users.identificationNumber')"
                name="identificationNumber"
                variant="static"
              />
            </div>
          </div>
        </div>
        <div v-if="enableUserGroups && userRole !== 'User'" class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label>{{ $t('users.userGroup') }}</label>
              <el-select v-model="model.userGroupId" clearable class="w-100" :class="selectClass" :placeholder="$t('users.selectUserGroup')">
                <el-option
                  v-for="group in userGroups"
                  :key="group.id"
                  :class="selectClass"
                  :label="group.name"
                  :value="group.id"
                />
              </el-select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <material-input-field
                id="firstName"
                v-model="model.firstName"
                :label="$t('users.firstName')"
                :placeholder="$t('users.firstName')"
                name="firstName"
                variant="static"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <material-input-field
                id="lastName"
                v-model="model.lastName"
                :label="$t('users.lastName')"
                :placeholder="$t('users.lastName')"
                name="lastName"
                variant="static"
              />
            </div>
          </div>
        </div>
        <div v-if="model.language" class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <material-input-field
                id="phoneNumber"
                v-model="model.phoneNumber"
                :label="$t('users.phoneNumber')"
                :placeholder="$t('users.phoneNumber')"
                name="phoneNumber"
                variant="static"
              />
            </div>
          </div>
        </div>
        <div v-if="userRole !== 'User'" class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <material-checkbox v-model="model.disabled">
                {{ $t('users.disabled') }}
              </material-checkbox>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-between mt-3">
          <div>
            <material-button v-if="userRole !== 'User' && !model.emailConfirmed" fill color="dark" type="standard" @click="handleReconfirmationEmail">
              {{ $t('common.reconfirmEmail') }}
            </material-button>
          </div>
          <div>
            <material-button fill color="dark" type="standard" @click="handleUserUpdate">
              {{ $t('common.save') }}
            </material-button>
          </div>
        </div>
      </card>
    </div>

    <!-- Tables Section (for admins) -->
    <card v-if="userRole !== 'User'" :title="cardTitle" class="tables-card">
      <template v-if="enableExcelExport && !isGeoTraceTab" #under-header>
        <div style="padding: 0 0 8px;">
          <el-button :icon="Download" size="small" @click="showExportDialog = true">{{ $t('dataExport.exportUserReservations') }}</el-button>
        </div>
      </template>
      <template #table-select>
        <NavPills
          :model-value="activeIndex"
          :items="navPillItems"
          @update:model-value="initTable"
        />
      </template>

      <TimesheetGeoTraces v-if="isGeoTraceTab" :key="`geo-${userId}`" :user-id="userId" />
      <paginated-table
        v-else-if="currentTableConfig"
        :columns="currentTableConfig.columns"
        :data="currentTableConfig.data"
        :pagination="currentTableConfig.pagination"
        :ready="currentTableConfig.ready"
        :allow-click="currentTableConfig.allowClick"
        :enable-edit="currentTableConfig.enableEdit"
        :enable-delete="currentTableConfig.enableDelete"
        :actions-inline="currentTableConfig.actionsInline"
        @row-click="currentTableConfig.onRowClick"
        @edit="currentTableConfig.onEdit"
        @update-page="currentTableConfig.onUpdatePage"
      />
    </card>

    <data-export-modal
      v-model:show="showExportDialog"
      type="userReservations"
      :user-id="userId"
      :show-external="model.role !== 'User'"
    />
  </div>
</template>

<script>
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import api from "../../services/api";
import { loadCustomerMeCached } from '../../services/customerMeHelper';
import { ElSelect, ElOption, ElButton } from 'element-plus';
import { Download } from '@element-plus/icons-vue';
import { shallowRef } from 'vue';
import { EventBus } from '../../components/event-bus.js';
import imageCompression from 'browser-image-compression';
import MaterialCheckbox from '../../components/MaterialCheckbox.vue';
import MaterialInputField from '../../components/MaterialInputField.vue';
import { defaultUserImage } from '../../components/Image Bytes/imagebytes';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import { HEAVY_MACHINERY_TOOL_GROUP_TYPE_ID } from '../HeavyMachines/heavyMachineHelper';
import Card from "../../components/Card.vue";
import PaginatedTable from "../../components/PaginatedTable.vue";
import NavPills from "../../components/NavPills.vue";
import MaterialButton from "../../components/MaterialButton.vue";
import DataExportModal from "../../components/DataExportModal.vue";
import TimesheetGeoTraces from "./TimesheetGeoTraces.vue";

export default {
  components: {
    ElSelect,
    ElOption,
    ElButton,
    MaterialCheckbox,
    MaterialInputField,
    Card,
    PaginatedTable,
    MaterialButton,
    NavPills,
    DataExportModal,
    TimesheetGeoTraces,
  },
  data() {
    return {
      model: {
        company: "",
        userName: "",
        email: "",
        phoneNumber: "",
        firstName: "",
        lastName: "",
        fullName: "",
        language: "",
        base64ImageData: "",
        identificationNumber: null,
        employeeTitle: null,
        emailConfirmed: false,
        role: "",
        disabled: false,
        userGroupId: null,
      },
      companyModel: {
        name: "",
        identificationNumber: "",
        base64ImageData: "",
      },
      userGroupModel: {
        name: "",
        identificationNumber: "",
      },
      enableUserGroups: false,
      userGroups: [],
      imagePreviewUrl: '',
      userInfoLoaded: false,
      roleOptions: ['Admin', 'User'],
      enableEdit: false,
      userRole: '',
      userId: '',
      currentUserId: '',
      disableCars: false,
      loanRentCustomer: false,
      enablePreCreatedProjectAssignments: false,
      enableDayProjectAssignments: false,
      enableProjectAssignmentTypes: false,

      userReservationTable: {
        title: this.$t('tools.myTools'),
        columns: [
          { prop: 'image', label: this.$t('common.image'), width: 80, mobileWidth: 100, mobile: true, isImage: true },
          { prop: 'tool', label: this.$t('common.tool'), minWidth: 200 },
          { prop: 'project', label: this.$t('common.project'), minWidth: 100 },
          { prop: 'daysLeft', label: this.$t('tools.daysLeft'), minWidth: 60 },
          { prop: 'reservationDate', label: this.$t('tools.reservation'), minWidth: 60 },
          { prop: 'quantity', label: this.$t('tools.quantity'), minWidth: 60 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.tool': 'tool', 'common.project': 'project', 'tools.daysLeft': 'daysLeft', 'tools.reservation': 'reservationDate', 'tools.quantity': 'quantity' } },
        ],
        data: [],
      },
      myCarsTable: {
        title: this.$t('cars.myCars'),
        columns: [
          { prop: 'name', label: this.$t('common.name'), minWidth: 200 },
          { prop: 'licencePlate', label: this.$t('cars.licencePlate'), minWidth: 200 },
          { prop: 'reservationDate', label: this.$t('tools.reservation'), minWidth: 80 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.name': 'name', 'cars.licencePlate': 'licencePlate', 'tools.reservation': 'reservationDate' } },
        ],
        data: [],
      },
      myHeavyMachinesTable: {
        title: this.$t('heavyMachines.myHeavyMachines'),
        columns: [
          { prop: 'name', label: this.$t('common.name'), minWidth: 200 },
          { prop: 'licencePlate', label: this.$t('heavyMachines.licencePlate'), minWidth: 200 },
          { prop: 'reservationDate', label: this.$t('tools.reservation'), minWidth: 80 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.name': 'name', 'heavyMachines.licencePlate': 'licencePlate', 'tools.reservation': 'reservationDate' } },
        ],
        data: [],
      },
      externalReservationsTable: {
        title: this.$t('projects.toolsLoanedRentedOnProject'),
        columns: [
          { prop: 'name', label: this.$t('common.tool'), minWidth: 200 },
          { prop: 'status', label: this.$t('tools.loanedRented'), minWidth: 80 },
          { prop: 'externalUser', label: this.$t('tools.loanedRentedTo'), minWidth: 90 },
          { prop: 'reservationDate', label: this.$t('tools.reservation'), minWidth: 80 },
          { prop: 'quantity', label: this.$t('tools.quantity'), minWidth: 60 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.tool': 'name', 'common.status': 'status', 'tools.loanedRentedTo': 'externalUser', 'tools.reservation': 'reservationDate', 'tools.quantity': 'quantity' } },
        ],
        data: [],
      },
      projectAssignmentsTable: {
        title: this.$t('projectAssignments.assignmentsWorked'),
        columns: [
          { prop: 'project', label: this.$t('common.project'), minWidth: 200 },
          { prop: 'date', label: this.$t('common.date'), minWidth: 100 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.project': 'project', 'common.date': 'date' } },
        ],
        data: [],
      },

      myToolsPagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      myCarsPagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      myHeavyMachinesPagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      externalReservationPagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      projectAssignmentsPagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      // Keep backward compatibility
      pagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },

      reservationDataReady: false,
      carsTableDataReady: false,
      heavyMachinesTableDataReady: false,
      externalReservationDataReady: false,
      projectAssignmentDataReady: false,

      activeIndex: 0,
      cardTitle: this.$t('tools.myTools'),
      Download: shallowRef(Download),
      showExportDialog: false,
      enableExcelExport: false,
    };
  },
  computed: {
    avatarSrc() {
      // Use the freshly-uploaded data URL (correct MIME type) if available,
      // otherwise fall back to the base64 thumbnail from the API.
      return this.imagePreviewUrl || ('data:image/png;base64,' + this.model.base64ImageData);
    },
    headerName() {
      // When user groups are enabled and the user belongs to one, show the group's
      // values in the header; otherwise fall back to the company values.
      return this.userGroupModel.name || this.companyModel.name;
    },
    headerIdentificationNumber() {
      return this.userGroupModel.identificationNumber || this.companyModel.identificationNumber;
    },
    isRoleSelectable() {
      return this.userRole !== 'User';
    },
    selectClass() {
      const darkMode = localStorage.getItem('darkMode');
      return darkMode === 'true' ? 'select-primary' : 'select-standard';
    },
    buttonClass() {
      const darkMode = localStorage.getItem('darkMode');
      return darkMode === 'true' ? 'btn-primary' : 'btn-standard';
    },
    myTablesCategories() {
      let categories = [
        { name: this.$t('tools.myTools'), icon: 'settings' },
      ];
      if (!this.disableCars) {
        categories.push({ name: this.$t('cars.myCars'), icon: 'directions_bus' });
        categories.push({ name: this.$t('heavyMachines.myHeavyMachines'), icon: 'agriculture' });
      }
      if (this.model.role !== 'User') {
        categories.push({ name: this.$t('projects.toolsLoanedRentedOnProject'), icon: 'directions_run' });
      }

      if (this.enablePreCreatedProjectAssignments || this.enableDayProjectAssignments) {
        categories.push({ name: this.$t('projectAssignments.assignmentsWorked'), icon: 'assignment' });
      }
      categories.push({ name: this.$t('users.clockInOutTrail'), icon: 'place', kind: 'geoTrace' });
      return categories;
    },
    isGeoTraceTab() {
      return this.myTablesCategories[this.activeIndex]?.kind === 'geoTrace';
    },
    filteredTablesCategories() {
      return this.myTablesCategories.filter(option => option.showUser || this.userRole !== 'User');
    },
    navPillItems() {
      return this.myTablesCategories.map(c => ({ label: c.name, icon: c.icon }));
    },
    tableConfigs() {
      const configs = [
        // Index 0: My Tools
        {
          columns: this.userReservationTable.columns,
          data: this.userReservationTable.data,
          pagination: this.myToolsPagination,
          ready: this.reservationDataReady,
          allowClick: true,
          enableEdit: false,
          enableDelete: false,
          actionsInline: false,
          onRowClick: this.goToTool,
          onEdit: () => {},
          onUpdatePage: this.myToolsUpdatePage,
        },
      ];

      // Dynamically add Cars if not disabled
      if (!this.disableCars) {
        configs.push({
          columns: this.myCarsTable.columns,
          data: this.myCarsTable.data,
          pagination: this.myCarsPagination,
          ready: this.carsTableDataReady,
          allowClick: true,
          enableEdit: false,
          enableDelete: false,
          actionsInline: false,
          onRowClick: this.goToCar,
          onEdit: () => {},
          onUpdatePage: this.myCarsUpdatePage,
        });
        configs.push({
          columns: this.myHeavyMachinesTable.columns,
          data: this.myHeavyMachinesTable.data,
          pagination: this.myHeavyMachinesPagination,
          ready: this.heavyMachinesTableDataReady,
          allowClick: true,
          enableEdit: false,
          enableDelete: false,
          actionsInline: false,
          onRowClick: this.goToHeavyMachine,
          onEdit: () => {},
          onUpdatePage: this.myHeavyMachinesUpdatePage,
        });
      }

      // External Reservations (for non-User roles)
      if (this.model.role !== 'User') {
        configs.push({
          columns: this.externalReservationsTable.columns,
          data: this.externalReservationsTable.data,
          pagination: this.externalReservationPagination,
          ready: this.externalReservationDataReady,
          allowClick: true,
          enableEdit: false,
          enableDelete: false,
          actionsInline: false,
          onRowClick: this.goToTool,
          onEdit: () => {},
          onUpdatePage: this.externalReservationUpdatePage,
        });
      }

      if (this.enablePreCreatedProjectAssignments || this.enableDayProjectAssignments) {
        configs.push({
          columns: this.projectAssignmentsTable.columns,
          data: this.projectAssignmentsTable.data,
          pagination: this.projectAssignmentsPagination,
          ready: this.projectAssignmentDataReady,
          allowClick: false,
          enableEdit: false,
          enableDelete: false,
          actionsInline: false,
          onRowClick: () => {},
          onEdit: () => {},
          onUpdatePage: this.projectAssignmentsUpdatePage,
        });
      }

      return configs;
    },
    currentTableConfig() {
      return this.tableConfigs[this.activeIndex];
    },
  },
  watch: {
    userInfoLoaded(newVal) {
      if (newVal && this.userRole !== 'User') {
        this.$nextTick(() => this.initTable(this.activeIndex));
      }
    },
  },
  mounted() {},
  created() {
    getCustomerConfig('EnableProjectAssignmentTypes').then(config => {
      this.enableProjectAssignmentTypes = config === '' ? false : config;
    });

    getCustomerConfig('EnableDayProjectAssignments').then(config => {
      this.enableDayProjectAssignments = config === '' ? false : config;
    });

    getCustomerConfig('EnablePreCreatedProjectAssignments').then(config => {
      this.enablePreCreatedProjectAssignments = config === '' ? false : config;
    });

    getCustomerConfig('DisableCars').then(config => {
      this.disableCars = config === '' ? false : config;
    });

    getCustomerConfig('DefaultPageSize').then(config => {
      const pageSize = config === '' ? 10 : config;
      this.pagination.perPage = pageSize;
      this.myToolsPagination.perPage = pageSize;
      this.myCarsPagination.perPage = pageSize;
      this.myHeavyMachinesPagination.perPage = pageSize;
      this.externalReservationPagination.perPage = pageSize;
      this.projectAssignmentsPagination.perPage = pageSize;
    });

    getCustomerConfig('EnableExcelExport').then(config => {
      this.enableExcelExport = config === '' ? false : config;
    });

    getCustomerConfig('EnableUserGroups').then(async config => {
      this.enableUserGroups = config === true;
      if (this.enableUserGroups) {
        try {
          const res = await api.get('/UserGroup');
          this.userGroups = res.data || [];
        } catch (error) {
          console.error('Unable to load user groups:', error); // eslint-disable-line no-console
        }
      }
    });

    getCustomerConfig('LoanRentCustomer').then(config => {
      this.loanRentCustomer = config === '' ? false : config;
      this.fetchUserData();
      this.fetchCustomerData();
    });
  },
  methods: {
    getProjectAssignmentTabIndex() {
      let index = 1; // Start after My Tools
      if (!this.disableCars) index += 2; // cars + vinnuvélar
      if (this.model.role !== 'User') index++;
      return index;
    },

    initTable(index) {
      this.activeIndex = index;

      // Reset all ready states
      this.reservationDataReady = false;
      this.carsTableDataReady = false;
      this.heavyMachinesTableDataReady = false;
      this.externalReservationDataReady = false;
      this.projectAssignmentDataReady = false;

      // Geo-trace tab handles its own data; just title the card and bail out.
      const category = this.myTablesCategories[index];
      if (category?.kind === 'geoTrace') {
        this.cardTitle = category.name;
        return;
      }

      // Determine which table to show based on index and config
      let tabIndex = 0;

      // My Tools (always index 0)
      if (index === tabIndex) {
        this.cardTitle = this.$t('tools.myTools');
        if (this.userReservationTable.data.length <= 0) {
          this.fetchUserReservations();
        } else {
          this.reservationDataReady = true;
        }
        return;
      }
      tabIndex++;

      // My Cars (if enabled)
      if (!this.disableCars) {
        if (index === tabIndex) {
          this.cardTitle = this.$t('cars.myCars');
          if (this.myCarsTable.data.length <= 0) {
            this.fetchCarsData();
          } else {
            this.carsTableDataReady = true;
          }
          return;
        }
        tabIndex++;

        if (index === tabIndex) {
          this.cardTitle = this.$t('heavyMachines.myHeavyMachines');
          if (this.myHeavyMachinesTable.data.length <= 0) {
            this.fetchHeavyMachinesData();
          } else {
            this.heavyMachinesTableDataReady = true;
          }
          return;
        }
        tabIndex++;
      }

      // External Reservations (for non-User roles)
      if (this.model.role !== 'User') {
        if (index === tabIndex) {
          this.cardTitle = this.$t('projects.toolsLoanedRentedOnProject');
          if (this.externalReservationsTable.data.length <= 0) {
            this.fetchExternalReservationData();
          } else {
            this.externalReservationDataReady = true;
          }
          return;
        }
        tabIndex++;
      }

      // Project Assignments
      if (this.enablePreCreatedProjectAssignments || this.enableDayProjectAssignments) {
        if (index === tabIndex) {
          this.cardTitle = this.$t('projectAssignments.assignmentsWorked');
          if (this.projectAssignmentsTable.data.length <= 0) {
            this.fetchProjectAssignmentData();
          } else {
            this.projectAssignmentDataReady = true;
          }
          return;
        }
      }
    },

    async fetchUserData() {
      const decodedToken = jwtDecode(Cookies.get('accessToken'));
      const CompanyName = `${decodedToken.CustomerName}`;
      this.userRole = `${decodedToken.role}`;
      this.currentUserId = decodedToken.UserIdentifier;
      this.userId = this.$route.params.userId ? this.$route.params.userId : decodedToken.UserIdentifier;

      try {
        const response = await api.get(`/User/${this.userId}`);
        this.model.company = CompanyName;
        this.model.userName = response.data.userName;
        this.model.email = response.data.email;
        this.model.phoneNumber = response.data.phoneNumber;
        this.model.firstName = response.data.firstName;
        this.model.fullName = response.data.firstName + " " + response.data.lastName;
        this.model.lastName = response.data.lastName;
        this.model.language = response.data.language;
        this.model.identificationNumber = response.data.identificationNumber;
        this.model.employeeTitle = response.data.employeeTitle;
        this.model.emailConfirmed = response.data.emailConfirmed;
        this.model.base64ImageData = response.data.userImageThumbnailBytes !== null ? response.data.userImageThumbnailBytes : defaultUserImage;
        this.model.role = response.data.role;
        this.model.disabled = response.data.disabled;
        this.model.userGroupId = response.data.userGroupId;
        this.userGroupModel.name = response.data.userGroupName || "";
        this.userGroupModel.identificationNumber = response.data.userGroupIdentificationNumber || "";

        if (this.loanRentCustomer) {
          if (this.model.role === 'User') {
            this.initTable(0);
          } else {
            if (this.disableCars) {
              this.initTable(1);
            } else {
              this.initTable(2);
            }
          }
        } else {
          this.initTable(0);
        }

        this.userInfoLoaded = true;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 2000 });
      }
    },

    async fetchCustomerData() {
      try {
        const customer = await loadCustomerMeCached();
        this.companyModel.name = customer.name;
        this.companyModel.identificationNumber = customer.identificationNumber;
        this.companyModel.base64ImageData = customer.logoBytes;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 2000 });
      }
    },

    async fetchUserReservations() {
      try {
        const response = await api.get(`/Tool/Reservations?userId=${this.userId}&currentPage=${this.myToolsPagination.currentPage}&pageSize=${this.myToolsPagination.perPage}`);
        this.myToolsPagination.total = response.data.totalItems;
        this.pagination.total = response.data.totalItems;
        this.userReservationTable.data = response.data.result.map(item => {
          let daysLeft = '';
          if (item.tool.maxReservationDays && item.tool.maxReservationDays !== 0) {
            const currentDate = new Date();
            const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
            const daysSinceReservation = Math.round((currentDate - new Date(item.reservedTimestamp)) / oneDayInMilliseconds);
            daysLeft = item.tool.maxReservationDays - daysSinceReservation;
          } else {
            daysLeft = '∞';
          }
          return {
            reservationId: item.id,
            toolId: item.tool.id,
            image: 'data:image/jpeg;base64,' + item.tool.toolImageThumbnailBytes,
            tool: `#${item.tool.customerToolId} ${item.tool.name}`,
            project: item.project.name,
            reservationDate: new Date(item.reservedTimestamp).toLocaleDateString('is-IS'),
            daysLeft: daysLeft,
            quantity: item.quantity
          };
        });
        this.reservationDataReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    async fetchCarsData() {
      try {
        const response = await api.get(`/ToolGroup/Reservations/?userId=${this.userId}&currentPage=${this.myCarsPagination.currentPage}&pageSize=${this.myCarsPagination.perPage}&toolGroupTypeId=1`);
        this.myCarsPagination.total = response.data.totalItems;
        this.pagination.total = response.data.totalItems;
        this.myCarsTable.data = response.data.result.map(item => {
          const licencePlateFilter = item.toolGroup.toolGroupProperties.filter(p => p.key === 'licencePlate');
          const licencePlate = licencePlateFilter.length > 0 ? licencePlateFilter[0].value : '';
          return {
            toolGroupId: item.toolGroupId,
            name: item.toolGroup.name,
            licencePlate: licencePlate,
            reservationDate: new Date(item.reservedTimestamp).toLocaleDateString('is-IS'),
          };
        });
        this.carsTableDataReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    async fetchHeavyMachinesData() {
      try {
        const response = await api.get(`/ToolGroup/Reservations/?userId=${this.userId}&currentPage=${this.myHeavyMachinesPagination.currentPage}&pageSize=${this.myHeavyMachinesPagination.perPage}&toolGroupTypeId=${HEAVY_MACHINERY_TOOL_GROUP_TYPE_ID}`);
        this.myHeavyMachinesPagination.total = response.data.totalItems;
        this.pagination.total = response.data.totalItems;
        this.myHeavyMachinesTable.data = response.data.result.map(item => {
          const licencePlateProperty = item.toolGroup.toolGroupProperties.find(p => p.key === 'licencePlate');
          return {
            toolGroupId: item.toolGroupId,
            name: item.toolGroup.name,
            licencePlate: licencePlateProperty ? licencePlateProperty.value : '',
            reservationDate: new Date(item.reservedTimestamp).toLocaleDateString('is-IS'),
          };
        });
        this.heavyMachinesTableDataReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    async fetchExternalReservationData() {
      try {
        const response = await api.get(`/Tool/Reservations/External?userId=${this.userId}&currentPage=${this.externalReservationPagination.currentPage}&pageSize=${this.externalReservationPagination.perPage}&excludeImages=true`);
        this.externalReservationPagination.total = response.data.totalItems;
        this.pagination.total = response.data.totalItems;
        this.externalReservationsTable.data = response.data.result.map(item => {
          const loanRent = item.rented ? this.$t('tools.rented') : this.$t('tools.loaned');
          return {
            toolId: item.tool.id,
            name: `#${item.tool.customerToolId} ${item.tool.name}`,
            externalUser: item.externalUser,
            status: loanRent,
            reservationDate: new Date(item.reservedTimestamp).toLocaleDateString('is-IS'),
            quantity: item.quantity
          };
        });
        this.externalReservationDataReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    async fetchProjectAssignmentData() {
      try {
        const response = await api.get(`/Project/Assignments/Paginated?userId=${this.userId}&currentPage=${this.projectAssignmentsPagination.currentPage}&pageSize=${this.projectAssignmentsPagination.perPage}`);
        this.projectAssignmentsPagination.total = response.data.totalItems;
        this.pagination.total = response.data.totalItems;
        this.projectAssignmentsTable.data = response.data.result.map(item => {
          return {
            project: this.enableProjectAssignmentTypes && item.projectAssignmentType !== null
              ? `${item.project.name} - ${item.projectAssignmentType.type}`
              : item.project.name,
            date: new Date(item.startTime).toLocaleDateString('is-IS'),
          };
        });
        this.projectAssignmentDataReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    edit() {
      this.enableEdit = true;
    },

    cancel() {
      this.enableEdit = false;
    },

    handleReconfirmationEmail() {
      api.get(`/identity/ResendConfirmationEmail?userId=${this.userId}`)
        .then(response => {
          this.$toast.success(response.data.informationString, { timeout: 1500 });
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.failedToSendReconfirmEmail'), { timeout: 2000 });
        });
    },

    async handleUserUpdate() {
      // Check if the user is trying to downgrade themselves from admin to user
      if (this.userId === this.currentUserId && this.userRole === 'Admin' && this.model.role === 'User') {
        this.$toast.warning(this.$t('common.cannotDowngradeYourself'), { timeout: 2000 });
        return;
      }

      try {
        const payload = { ...this.model };
        if (this.enableUserGroups) {
          // 0 = explicit unassign (null would mean "leave unchanged" on the backend)
          payload.userGroupId = this.model.userGroupId ?? 0;
        } else {
          delete payload.userGroupId;
        }
        const response = await api.put(`/User/${this.userId}`, payload);
        this.model.email = response.data.email;
        this.model.phoneNumber = response.data.phoneNumber;
        this.model.firstName = response.data.firstName;
        this.model.fullName = response.data.firstName + " " + response.data.lastName;
        this.model.lastName = response.data.lastName;
        this.model.language = response.data.language;
        this.model.identificationNumber = response.data.identificationNumber;
        this.model.employeeTitle = response.data.employeeTitle;
        this.model.emailConfirmed = response.data.emailConfirmed;
        this.model.role = response.data.role;
        this.model.base64ImageData = response.data.userImageThumbnailBytes !== null ? response.data.userImageThumbnailBytes : defaultUserImage;
        this.model.userGroupId = response.data.userGroupId;
        const selectedGroup = this.userGroups.find(g => g.id === response.data.userGroupId);
        this.userGroupModel.name = selectedGroup ? selectedGroup.name : "";
        this.userGroupModel.identificationNumber = selectedGroup ? (selectedGroup.identificationNumber || "") : "";

        this.$toast.success(this.$t('users.updated'), { timeout: 1500 });
        this.enableEdit = false;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToUpdate'), { timeout: 2000 });
      }
    },

    onAvatarClick() {
      this.$refs.fileInput.click();
    },

    async handleFileChange(event) {
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      const file = await imageCompression(event.target.files[0], options);
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          // readAsDataURL gives "data:image/jpeg;base64,..." — correct MIME type for display.
          const dataUrl = reader.result;
          const base64 = dataUrl.split(',')[1];

          // Show the uploaded image immediately with the correct MIME type.
          this.imagePreviewUrl = dataUrl;

          api.put(`/User/${this.userId}`, { userImageBytes: base64 })
            .then(() => {
              this.$toast.success(this.$t('users.imageUpdated'), { timeout: 1500 });
              if (this.userId === this.currentUserId) {
                EventBus.$emit('user-image-updated', base64);
              }
            })
            .catch(error => {
              console.error('API request error:', error); // eslint-disable-line no-console
              this.$toast.danger(this.$t('common.unableToUpdate'), { timeout: 2000 });
            });
        };
        reader.readAsDataURL(file);
      }
    },

    goToTool(item) {
      this.$router.push({ name: 'Tool', params: { toolId: item.toolId } });
    },

    goToCar(item) {
      this.$router.push({ name: 'Car', params: { toolGroupId: item.toolGroupId.toString() } });
    },

    goToHeavyMachine(item) {
      this.$router.push({ name: 'Heavy Machine', params: { toolGroupId: item.toolGroupId.toString() } });
    },

    myToolsUpdatePage(item) {
      this.myToolsPagination.currentPage = item;
      this.pagination.currentPage = item;
      this.fetchUserReservations();
    },

    myCarsUpdatePage(item) {
      this.myCarsPagination.currentPage = item;
      this.pagination.currentPage = item;
      this.fetchCarsData();
    },

    myHeavyMachinesUpdatePage(item) {
      this.myHeavyMachinesPagination.currentPage = item;
      this.pagination.currentPage = item;
      this.fetchHeavyMachinesData();
    },

    externalReservationUpdatePage(item) {
      this.externalReservationPagination.currentPage = item;
      this.pagination.currentPage = item;
      this.fetchExternalReservationData();
    },

    projectAssignmentsUpdatePage(item) {
      this.projectAssignmentsPagination.currentPage = item;
      this.pagination.currentPage = item;
      this.fetchProjectAssignmentData();
    },

    getReservationLoanRentTranslation(reservationLoanRent) {
      switch (reservationLoanRent) {
        case "Loaned":
          return this.$t('tools.loaned');
        case "Rented":
          return this.$t('tools.rented');
        case "Reserved":
          return this.$t('tools.reserved');
        default:
          return 'N/A';
      }
    },
  }
};
</script>

<style scoped>
.profile-header {
  height: 180px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
}

.avatar {
  width: 130px;
  height: 130px;
  object-fit: cover;
  background: #fff;
  cursor: pointer;
  margin-top:15px;
}

.nav-item {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.opacity-9 {
  opacity: 0.9;
}
</style>
